import React, { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTriviaRush } from '../hooks/useTriviaRush';
import { useRushSounds } from '../hooks/useRushSounds';
import { useLayoutVariant } from '../hooks/useLayoutVariant';
import { useAuth } from '../hooks/useAuth';
import { post } from '../utils/api';
import { RushStartScreen } from '../components/trivia-rush/RushStartScreen';
import { QuestionCard } from '../components/trivia-rush/QuestionCard';
import { RushTimer } from '../components/trivia-rush/RushTimer';
import { RushGameOver } from '../components/trivia-rush/RushGameOver';
import { RushTopBar } from '../components/trivia-rush/RushTopBar';
import { RushWavyGrid } from '../components/trivia-rush/RushWavyGrid';
import { RushLeaderboardPanel } from '../components/trivia-rush/RushLeaderboardPanel';
import { KeyboardHints } from '../components/trivia-rush/KeyboardHints';
import { UserModal } from '../components/ui/modals/UserModal';
import { OnboardingSurveyGate } from '../components/OnboardingSurveyGate';

export const TriviaRushPage: React.FC = () => {
  const layout = useLayoutVariant();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { state, currentQuestion, startGame, submitAnswer, playAgain, showLeaderboard, toggleLeaderboard } = useTriviaRush(layout.type === 'desktop');
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedSide, setSelectedSide] = useState<'A' | 'B' | null>(null);
  const [battleLoading, setBattleLoading] = useState(false);

  useRushSounds({
    timeRemaining: state.timeRemaining,
    isTimerActive: state.status === 'playing',
    answerCorrect: state.lastResult?.correct ?? null,
  });

  const handleBattle = useCallback(async () => {
    if (!user) {
      setShowUserModal(true);
      return;
    }
    setBattleLoading(true);
    try {
      const response = await post<{ roomCode: string; inviteLink: string }>('/api/trivia-rush/battle/create', {});
      navigate(`/trivia-rush/battle/${response.data.roomCode}`);
    } catch (err) {
      // Show error on start screen via existing state
      setBattleLoading(false);
    }
  }, [user, navigate]);

  // Reset selection when question changes
  useEffect(() => {
    setSelectedSide(null);
  }, [currentQuestion]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (state.status !== 'playing') return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setSelectedSide('A');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setSelectedSide('B');
      } else if (e.key === 'Enter') {
        e.preventDefault();
        setSelectedSide(prev => {
          if (prev) submitAnswer(prev);
          return prev;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.status, submitAnswer]);

  const handlePanelSignIn = useCallback(() => {
    toggleLeaderboard();
    setShowUserModal(true);
  }, [toggleLeaderboard]);

  // Derive reveal states from lastResult
  const getRevealState = (side: 'A' | 'B') => {
    if (!state.lastResult) return null;
    return side === state.lastResult.correctAnswer ? 'correct' as const : 'wrong' as const;
  };

  const getRevealValue = (side: 'A' | 'B') => {
    if (!state.lastResult) return null;
    return side === 'A' ? state.lastResult.formattedValueA : state.lastResult.formattedValueB;
  };

  return (
    <>
      <Helmet>
        <title>Wallstreetle | Trivia Rush</title>
        <meta
          name="description"
          content="Test your stock market knowledge! Which company has the higher metric? Answer fast in this rapid-fire trivia game."
        />
      </Helmet>

      <RushTopBar
        isLeaderboardOpen={showLeaderboard}
        onLeaderboardToggle={toggleLeaderboard}
      />
      <div
        className="min-h-screen flex flex-col relative animate-fade-up opacity-0 bg-background"
      >
        <RushWavyGrid />
        <div
          className="w-full max-w-[480px] sm:max-w-[560px] md:max-w-[640px] lg:max-w-[720px] mx-auto px-4 pb-6 flex-1 flex flex-col relative z-[1]"
          style={{ paddingTop: 'calc(3.5rem + env(safe-area-inset-top))' }}
        >
          {/* Idle / Loading → Start Screen */}
          {(state.status === 'idle' || state.status === 'loading') && (
            <RushStartScreen
              onStart={startGame}
              onBattle={handleBattle}
              isLoading={state.status === 'loading'}
              isBattleLoading={battleLoading}
              error={state.error}
            />
          )}

          {/* Playing / Answered → Game UI */}
          {(state.status === 'playing' || state.status === 'answered') && currentQuestion && (
            <div className="flex-1 flex flex-col">
              {/* Score bar */}
              <div className="flex items-center justify-between mb-4">
                <span
                  className="text-sm font-medium"
                  style={{ color: 'hsl(var(--muted-foreground))' }}
                >
                  Score
                </span>
                <span
                  className="text-2xl font-bold font-mono"
                  style={{ color: 'var(--rush-purple-accent)' }}
                >
                  {state.score}
                </span>
              </div>

              {/* Timer */}
              <RushTimer
                timeRemaining={state.timeRemaining}
                timerDuration={state.timerDuration}
              />

              {/* Question text */}
              <div className="mt-8 mb-6 text-center">
                <p
                  className="text-base sm:text-lg"
                  style={{ color: 'hsl(var(--muted-foreground))' }}
                >
                  Which company has the higher
                </p>
                <p
                  className="text-xl sm:text-2xl font-bold mt-1"
                  style={{ color: 'hsl(var(--foreground))', fontFamily: 'DM Serif Display, serif' }}
                >
                  {currentQuestion.metricDisplayName}?
                </p>
              </div>

              {/* Company cards side-by-side with VS divider */}
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3 md:gap-4">
                <QuestionCard
                  company={currentQuestion.stockA}
                  label="A"
                  onSelect={() => submitAnswer('A')}
                  disabled={state.status === 'answered'}
                  revealState={getRevealState('A')}
                  revealValue={getRevealValue('A')}
                  selected={selectedSide === 'A'}
                />

                <span
                  className="text-sm sm:text-base md:text-lg font-bold"
                  style={{ color: 'hsl(var(--muted-foreground))' }}
                >
                  VS
                </span>

                <QuestionCard
                  company={currentQuestion.stockB}
                  label="B"
                  onSelect={() => submitAnswer('B')}
                  disabled={state.status === 'answered'}
                  revealState={getRevealState('B')}
                  revealValue={getRevealValue('B')}
                  selected={selectedSide === 'B'}
                />
              </div>

              {/* Keyboard hints (desktop only) */}
              {layout.type === 'desktop' && <KeyboardHints />}

              {/* Streak indicator */}
              {state.score > 0 && (
                <div className="mt-6 text-center">
                  <span
                    className="text-sm font-medium"
                    style={{ color: 'var(--rush-correct)' }}
                  >
                    {state.score} correct streak
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Game Over */}
          {state.status === 'game-over' && (
            <RushGameOver
              score={state.score}
              onPlayAgain={playAgain}
              error={state.error}
              lastMetricName={currentQuestion?.metricDisplayName}
              lastQuestion={currentQuestion}
              industryContextA={state.lastResult?.industryContextA}
              industryContextB={state.lastResult?.industryContextB}
            />
          )}
        </div>

        {/* Play Classic CTA */}
        <div className="fixed bottom-4 left-4 z-10">
          <Link
            to="/"
            className="text-xs font-medium px-3 py-1.5 rounded-full border transition-opacity hover:opacity-80"
            style={{
              color: 'hsl(var(--foreground))',
              borderColor: 'hsl(var(--border))',
              backgroundColor: 'transparent',
            }}
          >
            Play Classic Wallstreetle
          </Link>
        </div>

        <p className="text-xs text-center px-6 py-4 max-w-xl mx-auto relative z-[1]" style={{ color: 'hsl(var(--muted-foreground))', opacity: 0.5 }}>
          Wallstreetle is for entertainment and educational purposes only and does not constitute financial advice. Always consult a licensed financial advisor for investment guidance specific to your personal situation. Data accuracy is not guaranteed and cannot be relied on to make investment decisions. Always conduct your own research before making investment decisions.
        </p>
      </div>

      {layout.type === 'desktop' && (
        <RushLeaderboardPanel
          isOpen={showLeaderboard}
          onSignInClick={handlePanelSignIn}
        />
      )}

      <UserModal
        isOpen={showUserModal}
        onClose={() => setShowUserModal(false)}
      />
      <OnboardingSurveyGate />
    </>
  );
};
